import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faStar, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from '../common/Button';
import { getVehiclePreview } from '../../api/vehicleAPI';

const ConsultaCard = styled.div`
  background: ${({ theme }) => theme.colors.lighter};
  border-radius: ${({ theme }) => theme.radii.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: 48px 40px;
  width: 100%;
  max-width: 500px;
  position: relative;
  overflow: hidden;
  transform-style: preserve-3d;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.accent} 100%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(108, 92, 231, 0.1) 0%, rgba(108, 92, 231, 0) 70%);
    transform: translate(-50%, -50%);
    z-index: 0;
  }

  form, .payment-methods, .satisfaction-guarantee {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 40px 24px;
  }
`;

const CardHeader = styled.div`
  margin-bottom: 24px;
  text-align: center;
  h3 {
    font-size: 28px;
    margin-bottom: 16px;
    color: ${({ theme }) => theme.colors.dark};
    font-weight: 800;
  }
`;

const StepsIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray};
  font-weight: 500;
  padding: 0 10px;
`;

const Step = styled.span`
  position: relative;
  padding-bottom: 8px;
  &.active {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background: ${({ theme }) => theme.colors.primary};
      border-radius: 2px;
    }
  }
`;

const PlacaInputContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const PlacaInputBox = styled.div`
  position: relative;
  background-color: #fff;
  border: 3px solid ${({ theme, hasError }) => hasError ? theme.colors.danger : theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.radii.md};
  height: 130px;
  width: 100%;
  transition: ${({ theme }) => theme.transitions.fast};
  transform-style: preserve-3d;

  &:focus-within {
    border-color: ${({ theme, hasError }) => hasError ? theme.colors.danger : theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme, hasError }) => hasError ? 'rgba(214, 48, 49, 0.2)' : 'rgba(108, 92, 231, 0.2)'};
    transform: translateY(-5px);
  }
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 14px;
  text-align: center;
  height: 16px;
  margin-bottom: 16px;
`;

const PlacaHeader = styled.div`
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.accent} 100%);
  border-top-left-radius: ${({ theme }) => theme.radii.sm};
  border-top-right-radius: ${({ theme }) => theme.radii.sm};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  height: 32px;
  line-height: 32px;
  text-align: center;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  letter-spacing: 0.5px;
`;

const PlacaInputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 98px;
`;

const PlacaInput = styled.input`
  border: none;
  font-family: 'Manrope', sans-serif;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: 5px;
  outline: none;
  text-align: center;
  text-transform: uppercase;
  width: 90%;
  background: transparent;
  color: ${({ theme }) => theme.colors.dark};
  caret-color: ${({ theme }) => theme.colors.primary};

  &::placeholder {
    color: #CDD9E1;
    opacity: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 32px;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
`;

const PaymentMethods = styled.div`
  margin: 20px 0;
  text-align: center;
  img {
    max-width: 250px;
    height: auto;
    opacity: 0.8;
  }
`;

const SatisfactionGuarantee = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray};
  margin-top: 16px;
  font-weight: 500;

  svg {
    color: ${({ theme }) => theme.colors.warning};
  }
`;

const PlacaForm = () => {
  const [placa, setPlaca] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { setVehicleQuery } = useContext(AppContext);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const shouldFocus = sessionStorage.getItem('focusPlacaInput');
    if (shouldFocus && inputRef.current) {
      inputRef.current.focus();
      sessionStorage.removeItem('focusPlacaInput');
    }
  }, []);

  const validarPlaca = (value) => {
    const placaSemHifen = value.replace('-', '').toUpperCase();
    const regexAntiga = /^[A-Z]{3}[0-9]{4}$/;
    const regexMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;
    return regexAntiga.test(placaSemHifen) || regexMercosul.test(placaSemHifen);
  };

  const handleChange = (e) => {
    let raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    const regexParcial = /^[A-Z]{0,3}([0-9]{0,1}[A-Z]{0,1}[0-9]{0,2})?$/;
    const regexAntigaParcial = /^[A-Z]{0,3}[0-9]{0,4}$/;

    if (!regexParcial.test(raw) && !regexAntigaParcial.test(raw)) return;

    let formatted = raw.length > 3 ? raw.slice(0, 3) + '-' + raw.slice(3) : raw;
    setPlaca(formatted);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const placaLimpa = placa.replace('-', '');

    if (!validarPlaca(placaLimpa)) {
      setError('Formato de placa inválido. Use ABC1234 ou ABC1D23.');
      return;
    }

    setIsLoading(true);
    setVehicleQuery({ placa: placaLimpa });

    try {
      const response = await getVehiclePreview(placaLimpa);
      if (response && response.Placa != null) {
        navigate(`/resultado/${placaLimpa}`);
      } else {
        throw new Error('Não foram encontrados dados para a placa informada.');
      }
    } catch (apiError) {
      console.error('Erro na consulta:', apiError);
      setError('Ocorreu um erro ao consultar a placa. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConsultaCard>
      <CardHeader>
        <h3>Consultar Placa</h3>
        <StepsIndicator>
          <Step className="active">1. Placa</Step>
          <Step>2. Pagamento</Step>
          <Step>3. Relatório</Step>
        </StepsIndicator>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <PlacaInputContainer>
          <PlacaInputBox hasError={!!error}>
            <PlacaHeader>
              <span>MERCOSUL</span>
              <span>BRASIL</span>
            </PlacaHeader>
            <PlacaInputWrapper>
              <PlacaInput
                ref={inputRef}
                type="text"
                placeholder="ABC1D23"
                maxLength="8"
                autoComplete="off"
                required
                value={placa}
                onChange={handleChange}
                disabled={isLoading}
              />
            </PlacaInputWrapper>
          </PlacaInputBox>
        </PlacaInputContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? (
            <><FontAwesomeIcon icon={faSpinner} spin /> Consultando...</>
          ) : (
            <><FontAwesomeIcon icon={faSearch} /> Consultar Agora</>
          )}
        </SubmitButton>
      </form>
      <PaymentMethods>
        <img 
          src="https://placehold.co/250x40/fafafa/636e72?text=PIX+%E2%80%A2+Cart%C3%A3o+%E2%80%A2+Boleto&font=manrope" 
          alt="Formas de pagamento: PIX, Cartão, Boleto" 
        />
      </PaymentMethods>
      <SatisfactionGuarantee>
        <FontAwesomeIcon icon={faStar} />
        <span>98% de satisfação ou seu dinheiro de volta</span>
      </SatisfactionGuarantee>
    </ConsultaCard>
  );
};

export default PlacaForm;
